import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { useLayoutEffect, useState } from "react";
import "react-calendar/dist/Calendar.css";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import { Resolver, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { savePdfUrls, setPagesLinks, setWebInfo } from "Redux/Actions/Index";
import Commonservice from "Services/CommonService";
import LoadButton from "Shared/Common/LoadButton";
import { setFaviconAndTitle } from "Utils/Common/CommonMethods";
import { dateFormatSetter } from "Utils/Common/Requisition";
import * as Yup from "yup";

// "expired"     → link is older than 24 hours
// "unavailable" → link is valid but order is deleted/rejected
type PageState = "loading" | "expired" | "unavailable" | "invalid" | "form" | "error";

interface VerifyFormData {
  dob: Date | null;
}

const validationSchema = Yup.object().shape({
  dob: Yup.date()
    .typeError("Please enter a valid date of birth")
    .nullable()
    .required("Date of birth is required")
    .max(new Date(), "Date of birth cannot be in the future"),
});

const VerifyResult = () => {
  const baseUrl = useSelector((state: any) => state.Reducer.baseUrl);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>("loading");
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [labId, setLabId] = useState<number>(0);
  const [requisitionOrderId, setRequisitionOrderId] = useState<number>(0);
  const [patientId, setPatientId] = useState<number>(0);
  const [labKey, setLabKey] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const searchParams = new URLSearchParams(window.location.search);
  const ticket = searchParams.get("ticket") ?? "";
  const ticketType = parseInt(searchParams.get("type") ?? "0", 10);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<VerifyFormData>({
    resolver: yupResolver(validationSchema) as Resolver<VerifyFormData>,
    defaultValues: { dob: null },
  });

  const dobValue = watch("dob");

  useLayoutEffect(() => {
    loadLogoUrl();
    validateTicket();
  }, []);

  const loadLogoUrl = () => {
    Commonservice.getLoginPageLogo()
      .then((res: any) => {
        const logo = res.data?.logo || res.data;
        setLogoUrl(typeof logo === "string" ? logo : "");
        dispatch(
          setWebInfo({
            smartLogoUrl: res.data?.smartLogo,
            title: res.data?.labName,
          })
        );
        setFaviconAndTitle(res.data?.smartLogo, res.data?.labName);
      })
      .catch(() => { });
  };

  const validateTicket = async () => {
    if (!ticket) {
      setPageState("invalid");
      return;
    }
    try {
      const ticketRes = await axios.post(
        `${baseUrl}/api/Account/ValidateResultRequestTicket`,
        { ticket, ticketType }
      );
      const ticketOuter = ticketRes.data;
      const ticketData = ticketOuter?.data;
      const resultNotFound = ticketOuter?.status === 404;
      const linkPermanentlyExpired = ticketOuter?.status === 410;


      if (resultNotFound) setPageState("unavailable");
      if (linkPermanentlyExpired) setPageState("expired");

      const resolvedRequisitionOrderId: number = ticketData.requisitionOrderId ?? 0;
      const resolvedPatientId: number = ticketData.patientId ?? 0;

      setLabId(ticketData.labId ?? 0);
      setRequisitionOrderId(resolvedRequisitionOrderId);
      setPatientId(resolvedPatientId);
      setLabKey(ticketData.labKey ?? "");
      setUserId(ticketData.userId ?? "");

      const infoRes = await axios.post(
        `${baseUrl}/api/Account/GetResultRequestInfo`,
        { requisitionOrderId: resolvedRequisitionOrderId, patientId: resolvedPatientId },
        { headers: { "X-Portal-Key": ticketData.labKey ?? "" } }
      );
      const infoData = infoRes.data?.data;

      setFirstName(infoData?.firstName ?? "");
      setLastName(infoData?.lastName ?? "");
      setPageState("form");
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 410) {
        setPageState("expired");
      } else if (status === 403 || status === 404) {
        setPageState("unavailable");
      } else {
        setPageState("invalid");
      }
    }
  };

  const handleDateChange = (value: any) => {
    const date = Array.isArray(value) ? value[0] : value;
    setValue("dob", dateFormatSetter(date), { shouldValidate: true });
  };

  const onSubmit = async ({ dob }: VerifyFormData) => {
    setIsSubmitting(true);

    const formattedDob = dob
      ? `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, "0")}-${String(dob.getDate()).padStart(2, "0")}`
      : "";

    const payload = {
      labId,
      requisitionOrderId,
      patientId,
      firstName,
      lastName,
      dob: formattedDob,
      ticket,
      ticketType,
    };

    try {
      const response = await axios.post(
        `${baseUrl}/api/Account/VerifyAndGetResultFile`,
        payload,
        { headers: { "X-Portal-Key": labKey, "UserId": userId, "PortalType": 4 } }
      );

      const body = response.data;
      const urls: string[] = Array.isArray(body?.data) ? body.data : [];

      if (urls.length > 0) {
        dispatch(savePdfUrls(urls));
        navigate("/docs-viewer");
      } else {
        toast.error(body?.message ?? "Something went wrong. Please try again.");
      }
    } catch (err: any) {
      const body = err.response?.data;
      toast.error(body?.message ?? "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const PageShell = ({ children }: { children: React.ReactNode }) => (
    <div
      className="d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed"
      style={{ backgroundImage: 'url("/media/illustrations/login/sketch-1.png")' }}
    >
      <div
        className="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20"
        style={{ minHeight: "100vh" }}
      >
        {children}
      </div>
    </div>
  );

  if (pageState === "loading") {
    return (
      <PageShell>
        <div className="w-lg-500px bg-body rounded shadow-sm p-10 mx-auto text-center">
          <div className="spinner-border text-primary mb-4" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <p className="text-muted">Verifying your link, please wait…</p>
        </div>
      </PageShell>
    );
  }

  if (pageState === "expired") {
    return (
      <PageShell>
        <div className="w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto text-center">
          {logoUrl && <img alt="Logo" src={logoUrl} className="h-45px mb-8" />}
          <i
            className="fa fa-exclamation-triangle text-danger mb-4 d-block"
            style={{ fontSize: "48px" }}
          />
          <h3 className="fw-bold mb-3">Link Expired</h3>
          <p className="text-muted">
            This link has expired. Please contact the laboratory for assistance.
          </p>
        </div>
      </PageShell>
    );
  }

  if (pageState === "unavailable") {
    return (
      <PageShell>
        <div className="w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto text-center">
          {logoUrl && <img alt="Logo" src={logoUrl} className="h-45px mb-8" />}
          <i
            className="fa fa-exclamation-triangle text-warning mb-4 d-block"
            style={{ fontSize: "48px" }}
          />
          <h3 className="fw-bold mb-3">Result Unavailable</h3>
          <p className="text-muted">
            Your result is not available to view. Please contact the laboratory for assistance.
          </p>
        </div>
      </PageShell>
    );
  }

  if (pageState === "invalid") {
    return (
      <PageShell>
        <div className="w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto text-center">
          {logoUrl && <img alt="Logo" src={logoUrl} className="h-45px mb-8" />}
          <i
            className="fa fa-exclamation-triangle text-danger mb-4 d-block"
            style={{ fontSize: "48px" }}
          />
          <h3 className="fw-bold mb-3">Invalid Link</h3>
          <p className="text-muted">
            This link is invalid. Please contact the laboratory for assistance.
          </p>
        </div>
      </PageShell>
    );
  }

  if (pageState === "error") {
    return (
      <PageShell>
        <div className="w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto text-center">
          {logoUrl && <img alt="Logo" src={logoUrl} className="h-45px mb-8" />}
          <i
            className="fa fa-times-circle text-danger mb-4 d-block"
            style={{ fontSize: "48px" }}
          />
          <h3 className="fw-bold mb-3">Something Went Wrong</h3>
          <p className="text-muted">
            We were unable to process your request. Please try again later or
            contact the laboratory for assistance.
          </p>
        </div>
      </PageShell>
    );
  }

  // "form" state — default render
  return (
    <PageShell>
      <div className="w-lg-550px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto">
        <form className="form w-100" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-10 text-center">
            {logoUrl && (
              <a href="#" className="mb-6 d-flex justify-content-center">
                <img alt="Logo" src={logoUrl} className="h-45px" />
              </a>
            )}
            <h2 className="fw-bold mb-3">Verify your identity</h2>
            <p className="text-muted">
              To view your lab results, please confirm your details below.
            </p>
          </div>

          <div className="mb-5">
            <label className="fw-500 mb-2">First name</label>
            <input
              type="text"
              className="form-control bg-light not-allowed"
              value={firstName}
              readOnly
              tabIndex={-1}
            />
          </div>

          <div className="mb-5">
            <label className="fw-500 mb-2">Last name</label>
            <input
              type="text"
              className="form-control bg-light not-allowed"
              value={lastName}
              readOnly
              tabIndex={-1}
            />
          </div>

          <div className="mb-5">
            <label className="required fw-500 mb-2">Date of birth</label>
            <DatePicker
              format="MM/dd/yyyy"
              dayPlaceholder="dd"
              monthPlaceholder="mm"
              yearPlaceholder="yyyy"
              className={`w-100 border rounded p-2 ${errors.dob ? "is-invalid" : ""}`}
              onChange={handleDateChange}
              value={dobValue ?? null}
              onKeyDown={(e) => e.preventDefault()}
            />
            {errors.dob && (
              <div className="text-danger mt-1 fs-7">{errors.dob.message}</div>
            )}
          </div>

          <div className="mb-6">
            <LoadButton
              loading={isSubmitting}
              btnText="View my results"
              loadingText="Verifying…"
              className="btn btn-success w-100 h-50px fs-5 rounded-pill"
              disabled={isSubmitting}
            />
          </div>

          <div className="text-center">
            <span style={{ color: "#c53030" }}>
              Having trouble?{" "}
              <span
                className="fw-semibold"
                style={{ color: "#c53030", cursor: "default" }}
              >
                Please contact the laboratory for assistance.
              </span>
            </span>
          </div>
        </form>
      </div>
    </PageShell>
  );
};

export default VerifyResult;
